/**
 * Compact country list with ISO-3166 alpha-2, dial code, and display name.
 * Not exhaustive — the ~60 most common countries for global SaaS. Add more as needed.
 */
export interface Country {
  code: string;   // ISO-3166 alpha-2, uppercase
  name: string;
  dial: string;   // starts with '+'
}

export const COUNTRIES: Country[] = [
  { code: "US", name: "United States", dial: "+1" },
  { code: "GB", name: "United Kingdom", dial: "+44" },
  { code: "IN", name: "India", dial: "+91" },
  { code: "CA", name: "Canada", dial: "+1" },
  { code: "AU", name: "Australia", dial: "+61" },
  { code: "DE", name: "Germany", dial: "+49" },
  { code: "FR", name: "France", dial: "+33" },
  { code: "IT", name: "Italy", dial: "+39" },
  { code: "ES", name: "Spain", dial: "+34" },
  { code: "NL", name: "Netherlands", dial: "+31" },
  { code: "BE", name: "Belgium", dial: "+32" },
  { code: "SE", name: "Sweden", dial: "+46" },
  { code: "NO", name: "Norway", dial: "+47" },
  { code: "DK", name: "Denmark", dial: "+45" },
  { code: "FI", name: "Finland", dial: "+358" },
  { code: "IE", name: "Ireland", dial: "+353" },
  { code: "PT", name: "Portugal", dial: "+351" },
  { code: "PL", name: "Poland", dial: "+48" },
  { code: "CH", name: "Switzerland", dial: "+41" },
  { code: "AT", name: "Austria", dial: "+43" },
  { code: "GR", name: "Greece", dial: "+30" },
  { code: "CZ", name: "Czech Republic", dial: "+420" },
  { code: "RO", name: "Romania", dial: "+40" },
  { code: "HU", name: "Hungary", dial: "+36" },
  { code: "BG", name: "Bulgaria", dial: "+359" },
  { code: "RU", name: "Russia", dial: "+7" },
  { code: "UA", name: "Ukraine", dial: "+380" },
  { code: "TR", name: "Turkey", dial: "+90" },
  { code: "IL", name: "Israel", dial: "+972" },
  { code: "AE", name: "United Arab Emirates", dial: "+971" },
  { code: "SA", name: "Saudi Arabia", dial: "+966" },
  { code: "EG", name: "Egypt", dial: "+20" },
  { code: "ZA", name: "South Africa", dial: "+27" },
  { code: "NG", name: "Nigeria", dial: "+234" },
  { code: "KE", name: "Kenya", dial: "+254" },
  { code: "CN", name: "China", dial: "+86" },
  { code: "HK", name: "Hong Kong", dial: "+852" },
  { code: "TW", name: "Taiwan", dial: "+886" },
  { code: "JP", name: "Japan", dial: "+81" },
  { code: "KR", name: "South Korea", dial: "+82" },
  { code: "SG", name: "Singapore", dial: "+65" },
  { code: "MY", name: "Malaysia", dial: "+60" },
  { code: "TH", name: "Thailand", dial: "+66" },
  { code: "ID", name: "Indonesia", dial: "+62" },
  { code: "PH", name: "Philippines", dial: "+63" },
  { code: "VN", name: "Vietnam", dial: "+84" },
  { code: "PK", name: "Pakistan", dial: "+92" },
  { code: "BD", name: "Bangladesh", dial: "+880" },
  { code: "LK", name: "Sri Lanka", dial: "+94" },
  { code: "NZ", name: "New Zealand", dial: "+64" },
  { code: "MX", name: "Mexico", dial: "+52" },
  { code: "BR", name: "Brazil", dial: "+55" },
  { code: "AR", name: "Argentina", dial: "+54" },
  { code: "CL", name: "Chile", dial: "+56" },
  { code: "CO", name: "Colombia", dial: "+57" },
  { code: "PE", name: "Peru", dial: "+51" },
];

export const COUNTRIES_BY_CODE: Record<string, Country> =
  COUNTRIES.reduce((acc, c) => ({ ...acc, [c.code]: c }), {});

/**
 * Best-effort IP-based country detection (free, no key required).
 * Falls back to "US" on failure.
 */
export async function detectCountry(): Promise<Country> {
  try {
    const r = await fetch("https://ipapi.co/json/", { headers: { Accept: "application/json" } });
    if (r.ok) {
      const d = await r.json();
      const iso = String(d.country_code || d.country || "").toUpperCase();
      if (iso && COUNTRIES_BY_CODE[iso]) return COUNTRIES_BY_CODE[iso];
    }
  } catch {
    /* ignore */
  }
  return COUNTRIES_BY_CODE["US"];
}

// Country → most-common tax-ID kind + optional label
export const TAX_KIND_BY_COUNTRY: Record<string, { kind: string; label: string; placeholder: string }> = {
  IN: { kind: "GST", label: "GSTIN", placeholder: "22AAAAA0000A1Z5" },
  US: { kind: "EIN", label: "EIN", placeholder: "12-3456789" },
  GB: { kind: "VAT", label: "VAT number", placeholder: "GB123456789" },
  DE: { kind: "VAT", label: "USt-IdNr.", placeholder: "DE123456789" },
  FR: { kind: "VAT", label: "TVA", placeholder: "FRXX999999999" },
  IT: { kind: "VAT", label: "P.IVA", placeholder: "IT12345678901" },
  ES: { kind: "VAT", label: "NIF-IVA", placeholder: "ESA12345678" },
  NL: { kind: "VAT", label: "BTW-nummer", placeholder: "NL123456789B01" },
  IE: { kind: "VAT", label: "VAT", placeholder: "IE1234567X" },
  AU: { kind: "ABN", label: "ABN", placeholder: "12 345 678 901" },
  CA: { kind: "GST", label: "GST/HST", placeholder: "123456789 RT0001" },
  BR: { kind: "CNPJ", label: "CNPJ", placeholder: "12.345.678/0001-95" },
  MX: { kind: "RFC", label: "RFC", placeholder: "XAXX010101000" },
  SG: { kind: "GST", label: "GST number", placeholder: "M12345678A" },
  NZ: { kind: "GST", label: "GST", placeholder: "123-456-789" },
  ZA: { kind: "VAT", label: "VAT reg.", placeholder: "4123456789" },
  AE: { kind: "TRN", label: "TRN", placeholder: "100123456700003" },
};
